import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, CalendarDays, List } from "lucide-react";
import SEO from "../components/SEO";
import BookingDialog from "../components/BookingDialog";
import WeekCalendar from "../components/classes/WeekCalendar";
import ClassDetailsPanel from "../components/classes/ClassDetailsPanel";
import MobileAgenda from "../components/classes/MobileAgenda";
import { API } from "../lib/api";
import { track } from "../lib/analytics";
import {
  todayVan, weekStart, weekDates, fmtRange, fmtShort, occurrencesForWeek,
  ageMatches, weekdayOf, fmtTime, programColor, isFull, isBookingClosed,
  findNextAvailableWeek, WEEKDAYS,
} from "../lib/calendarUtils";

const AGE_BUCKETS = [
  { value: "all", label: "All ages" },
  { value: "3-5", label: "Ages 3–5" },
  { value: "6-9", label: "Ages 6–9" },
  { value: "10-16", label: "Ages 10–16" },
];
const TYPE_LABELS = {
  weekly_class: "Weekly Class", camp: "Camp", workshop: "Workshop",
  trial: "Trial", drop_in: "Drop-in", event: "Event",
};
const fmtMoney = (n) => (typeof n === "number" ? `$${n.toFixed(2)}` : "—");

const selectCls =
  "rounded-full border border-slate-300 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#00A6B2]/40";

function ListView({ occurrences, onSelect, selectedKey }) {
  if (occurrences.length === 0) return null;
  return (
    <div className="grid gap-2.5" data-testid="classes-list-view">
      {occurrences.map((o) => {
        const s = o.schedule;
        const c = programColor(s);
        const full = isFull(s);
        const closed = isBookingClosed(s);
        const p = s.pricing || {};
        const sel = o.key === selectedKey;
        return (
          <button
            key={o.key}
            type="button"
            onClick={() => onSelect(o)}
            data-testid={`list-slot-${o.key}`}
            className={`text-left rounded-2xl border border-l-4 ${c.border} bg-white p-4 flex flex-wrap items-center gap-x-5 gap-y-1 transition-shadow hover:shadow-sm ${
              sel ? "ring-2 ring-[#123B6D]" : "border-slate-200"
            }`}
          >
            <div className="min-w-0 flex-1">
              <p className="font-display font-extrabold text-sm tracking-tight" style={{ color: "#123B6D" }}>{s.title}</p>
              <p className="text-xs text-slate-600 mt-0.5">
                {weekdayOf(o.date)} · {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
                {s.age_range ? ` · Ages ${s.age_range}` : ""}
                {s.location ? ` · ${s.location}` : ""}
              </p>
            </div>
            <p className="text-sm font-bold text-slate-800">
              {s.billing_type === "subscription" ? `${fmtMoney(p.monthly_amount)}/mo` : fmtMoney(p.total_due_today)}
            </p>
            <span
              className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${
                closed || full ? "bg-slate-200 text-slate-600" : "bg-emerald-50 text-emerald-700"
              }`}
            >
              {closed ? "Booking closed" : full ? "Full" : `${s.seats_available} seats`}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ClassesPage() {
  const today = todayVan();
  const [state, setState] = useState({ loading: true, items: [], error: false });
  const [wkStart, setWkStart] = useState(weekStart(today));
  const [manualNav, setManualNav] = useState(false);
  const [view, setView] = useState("week");
  const [filters, setFilters] = useState({ age: "all", type: "all", day: "all" });
  const [selected, setSelected] = useState(null);
  const [booking, setBooking] = useState(null);

  const load = () =>
    fetch(`${API}/site/schedules`)
      .then((r) => { if (!r.ok) throw new Error("bad"); return r.json(); })
      .then((d) => setState({ loading: false, items: d.items || [], error: false }))
      .catch(() => setState({ loading: false, items: [], error: true }));

  useEffect(() => { load(); track("page_view", { path: "/classes" }); }, []);

  const dates = useMemo(() => weekDates(wkStart), [wkStart]);
  const types = useMemo(
    () => [...new Set(state.items.map((s) => s.schedule_type).filter(Boolean))],
    [state.items]
  );

  const filtered = useMemo(
    () =>
      state.items.filter(
        (s) =>
          ageMatches(s.age_range, filters.age) &&
          (filters.type === "all" || s.schedule_type === filters.type)
      ),
    [state.items, filters.age, filters.type]
  );

  const occurrences = useMemo(() => {
    const occs = occurrencesForWeek(filtered, dates);
    return filters.day === "all" ? occs : occs.filter((o) => weekdayOf(o.date) === filters.day);
  }, [filtered, dates, filters.day]);

  // Next week (from today, or beyond the viewed week) with an upcoming occurrence — bounded 52-week search
  const nextAvailableWeek = useMemo(() => {
    if (state.loading || !filtered.length) return null;
    const from = wkStart > today ? addWeek(wkStart, 1) : today;
    return findNextAvailableWeek(filtered, from, filters.day);
  }, [state.loading, filtered, filters.day, wkStart, today]);

  // Auto-advance: if the viewed week has no upcoming classes and the user hasn't
  // manually navigated, jump forward to the first week that does (no request loop —
  // pure client-side search over already-loaded schedules).
  useEffect(() => {
    if (state.loading || manualNav) return;
    const hasUpcoming = occurrences.some((o) => o.date >= today);
    if (hasUpcoming) return;
    const next = findNextAvailableWeek(filtered, today, filters.day);
    if (next && next !== wkStart) setWkStart(next);
  }, [state.loading, manualNav, occurrences, filtered, filters.day, wkStart, today]);

  // Default selection: first upcoming bookable occurrence in the visible week
  useEffect(() => {
    if (selected && occurrences.some((o) => o.key === selected.key)) return;
    const first =
      occurrences.find((o) => o.date >= today && !isFull(o.schedule) && !isBookingClosed(o.schedule)) ||
      occurrences.find((o) => o.date >= today) ||
      occurrences[0] || null;
    setSelected(first);
  }, [occurrences]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasSchedules = state.items.length > 0;

  return (
    <>
      <SEO
        path="/classes"
        title="Classes & Booking | Nexora Learning & Development Centre"
        description="Browse the weekly class calendar and book Nexora classes, camps and workshops in Richmond BC. Live availability, secure payment, instant confirmation."
      />
      <section className="relative overflow-hidden" style={{ backgroundColor: "#FFFCF7" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-16 pb-8">
          <p className="eyebrow" style={{ color: "#00A6B2" }}>Classes & booking</p>
          <h1
            className="font-display text-4xl sm:text-5xl tracking-tight font-extrabold mt-3 leading-[1.06]"
            style={{ color: "#123B6D" }}
            data-testid="classes-page-headline"
          >
            Find a class that fits your week.
          </h1>
          <p className="mt-3 max-w-2xl text-base text-slate-700 leading-relaxed">
            Browse the live calendar, tap a class for details, and enroll online.
            Prices show tuition, online booking fee, and GST — no surprises.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {state.loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading classes…
          </div>
        ) : state.error ? (
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-10 text-center" data-testid="classes-error">
            <p className="font-display text-lg font-bold" style={{ color: "#123B6D" }}>Class schedules are temporarily unavailable.</p>
            <p className="mt-2 text-sm text-slate-600">
              Please try again shortly, or contact us at{" "}
              <a href="mailto:admissions@nexoracentre.ca" className="font-semibold text-[#0369A1]">admissions@nexoracentre.ca</a> or 604-655-5306.
            </p>
          </div>
        ) : !hasSchedules ? (
          <div className="rounded-[1.75rem] border border-slate-200 bg-white p-10 text-center" data-testid="classes-empty">
            <p className="font-display text-lg font-bold" style={{ color: "#123B6D" }}>New class schedules are coming soon.</p>
            <p className="mt-2 text-sm text-slate-600">
              In the meantime, you can <a href="/trial-class" className="font-semibold text-[#0369A1]">book a trial class</a> or{" "}
              <a href="/contact" className="font-semibold text-[#0369A1]">send us an inquiry</a> and we'll help you find the right fit.
            </p>
          </div>
        ) : (
          <>
            {/* Filters + view toggle */}
            <div className="flex flex-wrap items-center gap-2.5 mb-4" data-testid="classes-filters">
              <select value={filters.age} onChange={(e) => { setManualNav(false); setFilters((f) => ({ ...f, age: e.target.value })); }} className={selectCls} data-testid="filter-age-select">
                {AGE_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <select value={filters.type} onChange={(e) => { setManualNav(false); setFilters((f) => ({ ...f, type: e.target.value })); }} className={selectCls} data-testid="filter-type-select">
                <option value="all">All programs</option>
                {types.map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
              </select>
              <select value={filters.day} onChange={(e) => { setManualNav(false); setFilters((f) => ({ ...f, day: e.target.value })); }} className={selectCls} data-testid="filter-day-select">
                <option value="all">Any day</option>
                {WEEKDAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <div className="ml-auto hidden lg:inline-flex rounded-full border border-slate-300 bg-white p-0.5" data-testid="view-toggle">
                {[{ v: "week", Icon: CalendarDays, label: "Week" }, { v: "list", Icon: List, label: "List" }].map(({ v, Icon, label }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setView(v)}
                    data-testid={`view-toggle-${v}`}
                    className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
                      view === v ? "bg-[#123B6D] text-white" : "text-slate-600"
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Week navigation */}
            <div className="flex items-center gap-2 mb-4" data-testid="week-navigation">
              <button type="button" onClick={() => { setManualNav(true); setWkStart(weekStart(today)); }} data-testid="week-today-btn"
                className="rounded-full border-2 px-4 py-1.5 text-xs font-bold" style={{ borderColor: "#00A6B2", color: "#00A6B2" }}>
                Today
              </button>
              <button type="button" aria-label="Previous week" onClick={() => { setManualNav(true); setWkStart((w) => addWeek(w, -1)); }} data-testid="week-prev-btn"
                className="rounded-full border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" aria-label="Next week" onClick={() => { setManualNav(true); setWkStart((w) => addWeek(w, 1)); }} data-testid="week-next-btn"
                className="rounded-full border border-slate-300 bg-white p-2 text-slate-600 hover:bg-slate-50">
                <ChevronRight className="h-4 w-4" />
              </button>
              <p className="font-display font-extrabold text-sm sm:text-base tracking-tight ml-1" style={{ color: "#123B6D" }} data-testid="week-range-label">
                {fmtRange(wkStart)}
              </p>
              <p className="hidden sm:block text-[11px] text-slate-400 ml-auto">All times Pacific (Vancouver)</p>
            </div>

            {/* Desktop: calendar/list + details panel */}
            <div className="hidden lg:grid grid-cols-[1fr_360px] gap-6 items-start">
              <div>
                {occurrences.length === 0 ? (
                  <EmptyWeek
                    testId="week-empty"
                    nextWeek={nextAvailableWeek}
                    hasFilters={filters.age !== "all" || filters.type !== "all" || filters.day !== "all"}
                    onJump={(w) => { setManualNav(true); setWkStart(w); }}
                  />
                ) : view === "week" ? (
                  <WeekCalendar dates={dates} occurrences={occurrences} selectedKey={selected?.key} onSelect={setSelected} todayStr={today} />
                ) : (
                  <ListView occurrences={occurrences} onSelect={setSelected} selectedKey={selected?.key} />
                )}
              </div>
              <div className="sticky top-24">
                <ClassDetailsPanel occ={selected} onBook={setBooking} />
              </div>
            </div>

            {/* Mobile: agenda + bottom drawer */}
            <div className="lg:hidden">
              {occurrences.length === 0 ? (
                <EmptyWeek
                  testId="week-empty-mobile"
                  nextWeek={nextAvailableWeek}
                  hasFilters={filters.age !== "all" || filters.type !== "all" || filters.day !== "all"}
                  onJump={(w) => { setManualNav(true); setWkStart(w); }}
                />
              ) : (
                <MobileAgenda dates={dates} occurrences={occurrences} todayStr={today} onBook={setBooking} />
              )}
            </div>
          </>
        )}
      </section>

      <BookingDialog
        ctx={booking}
        onClose={() => setBooking(null)}
        onFilled={() => { setBooking(null); load(); }}
      />
    </>
  );
}

function EmptyWeek({ testId, nextWeek, onJump, hasFilters }) {
  return (
    <div className="rounded-[1.5rem] border border-slate-200 bg-white p-10 text-center" data-testid={testId}>
      <p className="font-display font-bold text-slate-700">No classes match this week.</p>
      {nextWeek ? (
        <>
          <p className="mt-1.5 text-xs text-slate-500">The next available classes are the week of {fmtShort(nextWeek)}.</p>
          <button
            type="button"
            onClick={() => onJump(nextWeek)}
            data-testid="jump-next-available-btn"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-xs font-bold text-white hover:opacity-90"
            style={{ backgroundColor: "#123B6D" }}
          >
            Jump to next available week <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </>
      ) : (
        <p className="mt-1.5 text-xs text-slate-500" data-testid="no-future-classes-msg">
          No upcoming classes were found in the next 12 months{hasFilters ? " with these filters. Try adjusting the filters above." : ". Please check back soon or contact us for upcoming programs."}
        </p>
      )}
    </div>
  );
}

function addWeek(startStr, n) {
  const [y, m, d] = startStr.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n * 7)).toISOString().slice(0, 10);
}
